import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'

interface TipoImovelData {
    tipo: string
    quantidade: number
    percentual: number
    valor_total: number
    valor_medio: number
}

interface VendasPorTipoData {
    tipo_id: string
    tipo_nome: string
    total_vendas: number
    valor_total: number
    valor_medio: number
}

interface UseTiposImoveisReturn {
    data: TipoImovelData[]
    vendasData: VendasPorTipoData[]
    loading: boolean
    error: string | null
    refetch: () => Promise<void>
}

export function useTiposImoveis(): UseTiposImoveisReturn {
    const [data, setData] = useState<TipoImovelData[]>([])
    const [vendasData, setVendasData] = useState<VendasPorTipoData[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchTiposImoveis = async () => {
        try {
            setLoading(true)
            setError(null)

            // Buscar dados de vendas por tipo usando a view criada
            const { data: vendasPorTipo, error: vendasError } = await supabase
                .from('vendas_por_tipo_imovel')
                .select('*')

            if (vendasError) {
                throw vendasError
            }

            // Processar dados de vendas
            const vendasProcessed: VendasPorTipoData[] = vendasPorTipo?.map(item => ({
                tipo_id: item.tipo_id,
                tipo_nome: item.tipo_nome,
                total_vendas: item.total_vendas || 0,
                valor_total: item.valor_total_vendas || 0,
                valor_medio: item.valor_medio_venda || 0
            })) || []

            setVendasData(vendasProcessed)

            // Calcular percentuais para o gráfico
            const total = vendasProcessed.reduce((sum, item) => sum + item.total_vendas, 0)

            const tiposData: TipoImovelData[] = vendasProcessed.map(item => ({
                tipo: item.tipo_nome,
                quantidade: item.total_vendas,
                percentual: total > 0 ? Math.round((item.total_vendas / total) * 100) : 0,
                valor_total: item.valor_total,
                valor_medio: item.valor_medio
            }))

            // Ordenar por quantidade (maior primeiro)
            tiposData.sort((a, b) => b.quantidade - a.quantidade)

            setData(tiposData)
        } catch (err) {
            console.error('Erro ao buscar tipos de imóveis:', err)
            setError(err instanceof Error ? err.message : 'Erro desconhecido')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTiposImoveis()
    }, [])

    return {
        data,
        vendasData,
        loading,
        error,
        refetch: fetchTiposImoveis
    }
}
